<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue'
import { Head } from '@inertiajs/vue3'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import { CreditCard, Calendar, User, AlertTriangle, ExternalLink, Check, X, Video, Users, FileText, ChevronRight } from 'lucide-vue-next'

interface Subscription {
  id: number
  teacher_name: string
  teacher_slug: string
  teacher_avatar: string | null
  tier_name: string
  tier_slug: string
  amount: number
  status: string
  current_period_start: string
  current_period_end: string
  created_at: string
  session_day: string | null
  session_time: string | null
}

const props = defineProps<{
  subscriptions: Subscription[]
}>()

const subscriptionList = ref<Subscription[]>([...props.subscriptions])
const showCancelModal = ref(false)
const cancelingSubscription = ref<Subscription | null>(null)
const cancelStep = ref<1 | 2>(1)
const selectedReason = ref('')
const additionalComments = ref('')
const isCanceling = ref(false)
const successMessage = ref('')
const errorMessage = ref('')

const cancellationReasons = [
  { id: 'too_expensive', label: 'Too expensive for my budget' },
  { id: 'not_using', label: "I'm not using it enough" },
  { id: 'found_alternative', label: 'Found a different tutor/service' },
  { id: 'quality', label: 'Not satisfied with the sessions' },
  { id: 'scheduling', label: 'Session times don\'t work for me' },
  { id: 'temporary', label: 'Taking a break (planning to return)' },
  { id: 'completed', label: 'Achieved my learning goals' },
  { id: 'other', label: 'Other reason' },
]

const activeSubscriptions = computed(() =>
  subscriptionList.value.filter(s => s.status === 'active' || s.status === 'cancelling')
)

const pastSubscriptions = computed(() =>
  subscriptionList.value.filter(s => s.status !== 'active' && s.status !== 'cancelling')
)

const combinedReason = computed(() => {
  const reason = cancellationReasons.find(r => r.id === selectedReason.value)
  let result = reason?.label || selectedReason.value
  if (additionalComments.value.trim()) {
    result += ` | Comment: ${additionalComments.value.trim()}`
  }
  return result
})

const canSubmitCancellation = computed(() => {
  return selectedReason.value !== ''
})

function getTierColor(tier: string): string {
  switch (tier) {
    case 'gold':
      return 'bg-amber-100 text-amber-800 border-amber-200'
    case 'silver':
      return 'bg-gray-100 text-gray-800 border-gray-200'
    default:
      return 'bg-blue-100 text-blue-800 border-blue-200'
  }
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-800'
    case 'cancelling':
      return 'bg-amber-100 text-amber-800'
    case 'cancelled':
      return 'bg-red-100 text-red-800'
    case 'expired':
      return 'bg-gray-100 text-gray-600'
    default:
      return 'bg-gray-100 text-gray-600'
  }
}

function getTierIcon(tier: string) {
  switch (tier) {
    case 'gold':
      return Video
    case 'silver':
      return Users
    default:
      return FileText
  }
}

function getTierDescription(tier: string): string {
  switch (tier) {
    case 'gold':
      return '1:1 live sessions + group sessions + content library'
    case 'silver':
      return 'Group live sessions + content library'
    default:
      return 'Content library access'
  }
}

function openCancelModal(subscription: Subscription) {
  cancelingSubscription.value = subscription
  cancelStep.value = 1
  selectedReason.value = ''
  additionalComments.value = ''
  showCancelModal.value = true
}

function closeCancelModal() {
  showCancelModal.value = false
  cancelingSubscription.value = null
  cancelStep.value = 1
  selectedReason.value = ''
  additionalComments.value = ''
}

function goToStep2() {
  cancelStep.value = 2
}

function goBackToStep1() {
  cancelStep.value = 1
}

const cancelPollingTimer = ref<ReturnType<typeof setTimeout> | null>(null)
const cancelPollingSubscriptionId = ref<number | null>(null)

onUnmounted(() => {
  if (cancelPollingTimer.value) {
    clearTimeout(cancelPollingTimer.value)
  }
})

async function confirmCancel() {
  if (!cancelingSubscription.value) return

  isCanceling.value = true
  errorMessage.value = ''

  const subscriptionId = cancelingSubscription.value.id

  try {
    await window.axios.post('/student/subscriptions/cancel', {
      subscription_id: subscriptionId,
      reason: combinedReason.value || null,
    })

    // Show cancelling state and close modal
    const index = subscriptionList.value.findIndex(s => s.id === subscriptionId)
    if (index !== -1) {
      subscriptionList.value[index].status = 'cancelling'
    }

    closeCancelModal()

    // Start polling after 5 seconds to verify cancellation
    cancelPollingSubscriptionId.value = subscriptionId
    pollCancellationStatus(subscriptionId, 0)
  } catch (error: any) {
    errorMessage.value = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to cancel subscription'
  } finally {
    isCanceling.value = false
  }
}

function pollCancellationStatus(subscriptionId: number, attempt: number) {
  const maxAttempts = 6
  const delay = attempt === 0 ? 5000 : 3000

  cancelPollingTimer.value = setTimeout(async () => {
    try {
      const { data } = await window.axios.post('/student/subscriptions/verify-cancellation', {
        subscription_id: subscriptionId,
      })

      if (data.status === 'cancelled') {
        // Confirmed cancelled
        const index = subscriptionList.value.findIndex(s => s.id === subscriptionId)
        if (index !== -1) {
          subscriptionList.value[index].status = 'cancelled'
        }
        cancelPollingSubscriptionId.value = null
        successMessage.value = 'Subscription cancelled successfully. Your access has been removed.'
        setTimeout(() => { successMessage.value = '' }, 5000)
        return
      }

      // Still pending — retry if we haven't exceeded max attempts
      if (attempt + 1 < maxAttempts) {
        pollCancellationStatus(subscriptionId, attempt + 1)
      } else {
        // Give up polling — mark as cancelled optimistically
        const index = subscriptionList.value.findIndex(s => s.id === subscriptionId)
        if (index !== -1) {
          subscriptionList.value[index].status = 'cancelled'
        }
        cancelPollingSubscriptionId.value = null
        successMessage.value = 'Subscription cancellation is being processed. It may take a moment to reflect.'
        setTimeout(() => { successMessage.value = '' }, 5000)
      }
    } catch {
      // Network error — mark as cancelled optimistically
      const index = subscriptionList.value.findIndex(s => s.id === subscriptionId)
      if (index !== -1) {
        subscriptionList.value[index].status = 'cancelled'
      }
      cancelPollingSubscriptionId.value = null
    }
  }, delay)
}
</script>

<template>
  <Head title="Subscriptions" />

  <DashboardLayout>
    <div class="space-y-6">
      <!-- Success Message -->
      <div v-if="successMessage" class="bg-green-50 border-2 border-green-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
          <Check class="w-5 h-5 text-white" />
        </div>
        <div>
          <p class="text-green-900 font-semibold">Success</p>
          <p class="text-green-700 text-sm">{{ successMessage }}</p>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="errorMessage" class="bg-red-50 border-2 border-red-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-4">
          <X class="w-5 h-5 text-white" />
        </div>
        <div>
          <p class="text-red-900 font-semibold">Error</p>
          <p class="text-red-700 text-sm">{{ errorMessage }}</p>
        </div>
      </div>

      <!-- Active Subscriptions -->
      <section class="bg-white rounded-2xl shadow-sm p-8">
        <div class="flex items-center gap-3 mb-6">
          <div class="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <CreditCard class="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h2 class="text-xl font-semibold text-gray-900">Active Subscriptions</h2>
            <p class="text-gray-500 text-sm">Your current tutor subscriptions</p>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="activeSubscriptions.length === 0" class="text-center py-12">
          <CreditCard class="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 class="text-lg font-medium text-gray-900 mb-2">No active subscriptions</h3>
          <p class="text-gray-500 mb-4">Subscribe to a tutor to get started with personalized learning</p>
          <a
            href="/student/explore"
            class="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            Browse Tutors
            <ExternalLink class="w-4 h-4 ml-2" />
          </a>
        </div>

        <!-- Subscription Cards -->
        <div v-else class="space-y-4">
          <div
            v-for="subscription in activeSubscriptions"
            :key="subscription.id"
            class="border border-gray-200 rounded-xl p-5 hover:border-gray-300 transition-colors"
          >
            <div class="flex items-start justify-between">
              <div class="flex items-start gap-4">
                <!-- Tutor Avatar -->
                <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                  <img
                    v-if="subscription.teacher_avatar"
                    :src="subscription.teacher_avatar"
                    :alt="subscription.teacher_name"
                    class="w-full h-full object-cover"
                  />
                  <User v-else class="w-7 h-7 text-gray-400" />
                </div>

                <div>
                  <div class="flex items-center gap-2 mb-1">
                    <h3 class="font-semibold text-gray-900">{{ subscription.teacher_name }}</h3>
                    <span
                      :class="[
                        'text-xs px-2 py-0.5 rounded-full font-medium border',
                        getTierColor(subscription.tier_slug)
                      ]"
                    >
                      {{ subscription.tier_name }}
                    </span>
                    <span
                      :class="[
                        'text-xs px-2 py-0.5 rounded-full font-medium',
                        getStatusColor(subscription.status)
                      ]"
                    >
                      {{ subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1) }}
                    </span>
                  </div>

                  <div class="flex items-center gap-4 text-sm text-gray-500 mt-2">
                    <span class="flex items-center gap-1">
                      <CreditCard class="w-4 h-4" />
                      £{{ subscription.amount.toFixed(2) }}/week
                    </span>
                    <span class="flex items-center gap-1">
                      <Calendar class="w-4 h-4" />
                      Renews {{ subscription.current_period_end }}
                    </span>
                  </div>

                  <div v-if="subscription.session_day" class="flex items-center gap-1 text-sm text-blue-600 mt-2">
                    <Calendar class="w-4 h-4" />
                    <span class="font-medium">{{ subscription.session_day }}{{ subscription.session_time ? ` at ${subscription.session_time}` : '' }}</span>
                  </div>

                  <p class="text-xs text-gray-400 mt-2">
                    Started {{ subscription.created_at }}
                  </p>
                </div>
              </div>

              <div class="flex items-center gap-2">
                <a
                  :href="`/student/tutors/${subscription.teacher_slug}`"
                  class="px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  View Tutor
                </a>
                <span
                  v-if="subscription.status === 'cancelling'"
                  class="px-3 py-1.5 text-sm font-medium text-amber-600 flex items-center gap-1.5"
                >
                  <svg class="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Cancelling...
                </span>
                <button
                  v-else
                  @click="openCancelModal(subscription)"
                  class="px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Past Subscriptions -->
      <section v-if="pastSubscriptions.length > 0" class="bg-white rounded-2xl shadow-sm p-8">
        <div class="flex items-center gap-3 mb-6">
          <div class="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
            <Calendar class="w-5 h-5 text-gray-600" />
          </div>
          <div>
            <h2 class="text-xl font-semibold text-gray-900">Past Subscriptions</h2>
            <p class="text-gray-500 text-sm">Your subscription history</p>
          </div>
        </div>

        <div class="space-y-3">
          <div
            v-for="subscription in pastSubscriptions"
            :key="subscription.id"
            class="flex items-center justify-between p-4 border border-gray-100 rounded-xl bg-gray-50"
          >
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                <img
                  v-if="subscription.teacher_avatar"
                  :src="subscription.teacher_avatar"
                  :alt="subscription.teacher_name"
                  class="w-full h-full object-cover"
                />
                <User v-else class="w-5 h-5 text-gray-400" />
              </div>
              <div>
                <div class="flex items-center gap-2">
                  <span class="font-medium text-gray-700">{{ subscription.teacher_name }}</span>
                  <span
                    :class="[
                      'text-xs px-2 py-0.5 rounded-full font-medium',
                      getStatusColor(subscription.status)
                    ]"
                  >
                    {{ subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1) }}
                  </span>
                </div>
                <span class="text-sm text-gray-500">
                  {{ subscription.tier_name }} - £{{ subscription.amount.toFixed(2) }}/week
                </span>
              </div>
            </div>
            <span class="text-sm text-gray-400">Ended {{ subscription.current_period_end }}</span>
          </div>
        </div>
      </section>

      <!-- Help Section -->
      <section class="bg-blue-50 rounded-2xl p-6 border border-blue-100">
        <h3 class="font-medium text-blue-900 mb-2">Need help with billing?</h3>
        <p class="text-sm text-blue-700">
          If you have questions about your subscription or billing, please contact our support team at
          <a href="mailto:support@bindle.co.uk" class="underline">support@bindle.co.uk</a>
        </p>
      </section>
    </div>

    <!-- Cancel Confirmation Modal -->
    <Teleport to="body">
      <div
        v-if="showCancelModal"
        class="fixed inset-0 z-50 overflow-y-auto"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <div class="flex items-center justify-center min-h-screen p-4">
          <!-- Background overlay -->
          <div
            class="fixed inset-0 bg-gray-500/75 transition-opacity"
            @click="closeCancelModal"
          ></div>

          <!-- Modal panel -->
          <div class="relative bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all w-full max-w-lg">
            <!-- Step 1: Confirmation -->
            <template v-if="cancelStep === 1">
              <div class="bg-white px-6 pt-6 pb-4">
                <div class="flex items-start gap-4">
                  <div class="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <AlertTriangle class="w-6 h-6 text-red-600" />
                  </div>
                  <div class="flex-1">
                    <h3 class="text-lg font-semibold text-gray-900">Cancel Subscription?</h3>
                    <p class="mt-2 text-sm text-gray-500">
                      Are you sure you want to cancel your subscription?
                    </p>
                  </div>
                </div>

                <!-- Subscription Details Card -->
                <div class="mt-5 bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div class="flex items-center gap-3 mb-3">
                    <div class="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                      <img
                        v-if="cancelingSubscription?.teacher_avatar"
                        :src="cancelingSubscription.teacher_avatar"
                        :alt="cancelingSubscription.teacher_name"
                        class="w-full h-full object-cover"
                      />
                      <User v-else class="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                      <p class="font-semibold text-gray-900">{{ cancelingSubscription?.teacher_name }}</p>
                      <p class="text-sm text-gray-500">{{ cancelingSubscription?.tier_name }} Plan</p>
                    </div>
                  </div>

                  <div class="space-y-2 text-sm">
                    <div class="flex justify-between">
                      <span class="text-gray-500">Weekly cost</span>
                      <span class="font-medium text-gray-900">£{{ cancelingSubscription?.amount.toFixed(2) }}</span>
                    </div>
                  </div>

                  <!-- What you'll lose -->
                  <div class="mt-4 pt-3 border-t border-gray-200">
                    <p class="text-xs font-medium text-gray-700 mb-2">What you'll lose immediately:</p>
                    <div class="flex items-center gap-2 text-sm text-gray-600">
                      <component :is="getTierIcon(cancelingSubscription?.tier_slug || '')" class="w-4 h-4 text-gray-400" />
                      <span>{{ getTierDescription(cancelingSubscription?.tier_slug || '') }}</span>
                    </div>
                  </div>
                </div>

                <p class="mt-4 text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2">
                  <strong>Note:</strong> Cancellation is immediate. You will lose access to all content and sessions straight away.
                </p>
              </div>

              <div class="bg-gray-50 px-6 py-4 flex justify-between">
                <button
                  @click="closeCancelModal"
                  type="button"
                  class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Keep Subscription
                </button>
                <button
                  @click="goToStep2"
                  type="button"
                  class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-1"
                >
                  Continue
                  <ChevronRight class="w-4 h-4" />
                </button>
              </div>
            </template>

            <!-- Step 2: Reason Selection -->
            <template v-else>
              <div class="bg-white px-6 pt-6 pb-4">
                <h3 class="text-lg font-semibold text-gray-900 mb-2">Why are you cancelling?</h3>
                <p class="text-sm text-gray-500 mb-4">
                  Please let us know why you're cancelling so we can improve our service.
                </p>

                <!-- Reason Dropdown -->
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Reason for cancellation <span class="text-red-500">*</span>
                  </label>
                  <select
                    v-model="selectedReason"
                    class="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm bg-white"
                  >
                    <option value="" disabled>Select a reason...</option>
                    <option
                      v-for="reason in cancellationReasons"
                      :key="reason.id"
                      :value="reason.id"
                    >
                      {{ reason.label }}
                    </option>
                  </select>
                </div>

                <!-- Additional Comments (Optional) -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Additional comments <span class="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <textarea
                    v-model="additionalComments"
                    rows="3"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                    placeholder="Any additional feedback you'd like to share..."
                  ></textarea>
                </div>
              </div>

              <div class="bg-gray-50 px-6 py-4 flex justify-between">
                <button
                  @click="goBackToStep1"
                  type="button"
                  class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <button
                  @click="confirmCancel"
                  :disabled="isCanceling || !canSubmitCancellation"
                  type="button"
                  class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                >
                  <svg v-if="isCanceling" class="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {{ isCanceling ? 'Cancelling...' : 'Confirm Cancellation' }}
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </Teleport>
  </DashboardLayout>
</template>
